/* eslint-disable @typescript-eslint/no-var-requires */
import * as React from "react";
import {
  createMuiTheme,
  responsiveFontSizes,
  ThemeProvider,
  Zoom,
  CssBaseline,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { LocalCache, SessionCache } from "./helpers/cache";
import useConstructor from "./core/hooks/useConstructor";
import { useHubXSubscribe } from "./HubX";
import { EVENT_DARK_MODE } from "./Events";
import { APP_DEFAULT_ENV_STR } from "./core/constants/appGlobals";

/** * Custom Font definition ***/
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LindeDaxRegWebWOFF = require("./assets/fonts/Linde/WOFF/Fonts/LindeDaxRegWeb.woff") as any;

export const LINDE_REGULAR = {
  fontFamily: "Linde",
  fontStretch: "normal" as const,
  fontStyle: "normal" as const,
  fontWeight: 500,
  src: `
    local('Linde'),
    url(${LindeDaxRegWebWOFF}) format('woff')
  `,
  unicodeRange: `U+20-7E, U+A0-107, U+10A-113, U+116-11B,
  U+11E-123, U+126-127, U+12A-12B, U+12E-131,
  U+136-137, U+139-13E, U+141-148, U+14C-14D,
  U+150-15B, U+15E-165, U+16A-16B, U+16E-173,
  U+178-17E, U+192, U+218-21B, U+2C6-2C7,
  U+2C9, U+2D8-2DD, U+300-304, U+306-308,
  U+30A-30C, U+312-313, U+326-327, U+3BC,
  U+2013-2014, U+2018-201A, U+201C-201E, U+2020-2022,
  U+2026, U+2030, U+2039-203A, U+2044,
  U+20AC, U+2122, U+2212, U+2215,
  U+2219, U+2264-2265, U+25AF, U+FB01-FB02`,
};
/** *****/

// Allow changes to the Material global Theme object;
declare module "@material-ui/core/styles/createMuiTheme" {
  interface Theme {
    customPalette: {
      text: {
        primary: React.CSSProperties["color"];
        darkgrey: React.CSSProperties["color"];
        boldgrey: React.CSSProperties["color"];
        lightgrey: React.CSSProperties["color"];
        link: React.CSSProperties["color"];
        linkHover: React.CSSProperties["color"];
        linkContrast: React.CSSProperties["color"];
        linkHoverContrast: React.CSSProperties["color"];
        error: React.CSSProperties["color"];
      };
      svg: {
        lightgrey: React.CSSProperties["color"];
      };
      border: {
        lightgray?: React.CSSProperties["color"];
        gray: React.CSSProperties["color"];
      };
      levels: {
        veryLow: React.CSSProperties["color"];
        low: React.CSSProperties["color"];
        normal: React.CSSProperties["color"];
        high: React.CSSProperties["color"];
      };
    };
    customPaper: {
      background: {
        cardBackground: React.CSSProperties["color"];
        cardBackgroundLight: React.CSSProperties["color"];
        cardBackgroundLightContrast: React.CSSProperties["color"];
        cardBackgroundLightContrastWhite: React.CSSProperties["color"];
        cardBackgroundLightVariant: React.CSSProperties["color"];
        cardBackgroundLightVariantDark?: React.CSSProperties["color"];
        cardBackgroundSelected: React.CSSProperties["color"];
        cardBackgroundTable: React.CSSProperties["color"];
        tankContainer: React.CSSProperties["color"];
        contrast?: React.CSSProperties["color"];
        backgroundLogo?: React.CSSProperties["color"];
        cardBackgroundLightUltraDarkContrastWhite?: React.CSSProperties["color"];
        tankContainerBackground?: React.CSSProperties["color"];
      };
    };
  }
  // allow configuration using `createMuiTheme`
  interface ThemeOptions {
    customPalette?: {
      text?: {
        primary?: React.CSSProperties["color"];
        darkgrey?: React.CSSProperties["color"];
        boldgrey?: React.CSSProperties["color"];
        lightgrey?: React.CSSProperties["color"];
        link?: React.CSSProperties["color"];
        linkHover?: React.CSSProperties["color"];
        linkContrast: React.CSSProperties["color"];
        linkHoverContrast: React.CSSProperties["color"];
        error: React.CSSProperties["color"];
      };
      svg?: {
        lightgrey?: React.CSSProperties["color"];
      };
      border?: {
        lightgray?: React.CSSProperties["color"];
        gray?: React.CSSProperties["color"];
      };
      levels?: {
        veryLow?: React.CSSProperties["color"];
        low?: React.CSSProperties["color"];
        normal?: React.CSSProperties["color"];
        high?: React.CSSProperties["color"];
      };
    };
    customPaper?: {
      background?: {
        cardBackground?: React.CSSProperties["color"];
        cardBackgroundLight?: React.CSSProperties["color"];
        cardBackgroundLightContrast?: React.CSSProperties["color"];
        cardBackgroundLightContrastWhite?: React.CSSProperties["color"];
        cardBackgroundLightVariant?: React.CSSProperties["color"];
        cardBackgroundLightVariantDark?: React.CSSProperties["color"];
        cardBackgroundSelected?: React.CSSProperties["color"];
        cardBackgroundTable?: React.CSSProperties["color"];
        tankContainer?: React.CSSProperties["color"];
        contrast?: React.CSSProperties["color"];
        backgroundLogo?: React.CSSProperties["color"];
        cardBackgroundLightUltraDarkContrastWhite?: React.CSSProperties["color"];
        tankContainerBackground?: React.CSSProperties["color"];
      };
    };
    text?: {
      contrast?: React.CSSProperties["color"];
    };
  }
}

/** * Theme customization start ***/

// Theme Props
type Props = {
  children?: React.ReactChild;
};

// shared values;
export const APP_PRIMARY_COLOR = "#00AB66";
const RTUPrimaryColor = APP_PRIMARY_COLOR;
const linkColor = "#00A0E1";
const linkHoverColor = "#007AB9";
export const APP_WHITE = "#FFFFFF";
const appError = "#E1000F";
const appWarning = "#FBD249";
const selectBorder = "1px solid #B9CDD7";
export const LEVEL_COLOR_VERY_LOW = appError;
export const LEVEL_COLOR_LOW = appWarning;
export const LEVEL_COLOR_NORMAL = RTUPrimaryColor;
export const CHART_PRIMARY_COLOR = "#7cb5ec";

const CARD_BACKGROUND_ULTRA_DARK = "rgb(48, 48, 48)";

const CARD_BACKGROUND_DARK = "#424242";
const CARD_BACKGROUND_LIGHT = "#E7E7E7"; //#E7E7E7

const CARD_BACK = "#424242";
const CARD_BACK_LIGHT = "#6E7878";

const CARD_BACKGROUND_SELECTED_DARK = "#303030";
const CARD_BACKGROUND_SELECTED_LIGHT = "#B6BBBB";

const DARK_TANK_BACKGROUND = "#424242";
const LIGHT_TANK_BACKGROUND = "#ffffff";

function RTUThemeProvider(props: Props) {
  const [prefersDarkMode, setPrefersDarkMode] = React.useState<boolean>(false);
  const lcache = new LocalCache();
  const scache = new SessionCache();
  scache.set("environment", process.env.REACT_APP_LOG_ENV ?? APP_DEFAULT_ENV_STR);

  const getFromCache = () => {
    const prefersDark = lcache.get<boolean>("prefersDarkMode") || false;
    setPrefersDarkMode(prefersDark);
  };

  useConstructor(async () => {
    getFromCache();
  });

  useHubXSubscribe(EVENT_DARK_MODE, (isDark: boolean) => {
    setPrefersDarkMode(isDark);
  });

  const defaultTheme = React.useMemo(() => createMuiTheme(), []);

  let RTUTheme = React.useMemo(
    () =>
      createMuiTheme({
        typography: {
          fontFamily: ["Linde", "Arial", "Roboto", "-apple-system", "sans-serif"].join(","),
        },
        palette: {
          type: prefersDarkMode ? "dark" : "light",
          primary: {
            main: RTUPrimaryColor,
          },
          error: {
            main: "#E1000F",
          },
          text: {
            secondary: prefersDarkMode ? "#FFF" : "#717171",
          }
        },
        customPaper: {
          background: {
            cardBackground: prefersDarkMode ? CARD_BACK : CARD_BACK_LIGHT,
            cardBackgroundSelected: prefersDarkMode
              ? CARD_BACKGROUND_SELECTED_DARK
              : CARD_BACKGROUND_SELECTED_LIGHT,
            cardBackgroundLight: prefersDarkMode ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT,
            cardBackgroundLightContrastWhite: prefersDarkMode ? CARD_BACKGROUND_DARK : "#fff",
            // cardBackgroundLightInverted: prefersDarkMode ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT,
            cardBackgroundLightVariant: "#D7DAE2",
            cardBackgroundLightVariantDark: prefersDarkMode ? "#565555" : "#D7DAE2",
            cardBackgroundLightContrast: prefersDarkMode ? CARD_BACKGROUND_ULTRA_DARK : "#F5F5F5",
            cardBackgroundLightUltraDarkContrastWhite: prefersDarkMode ? CARD_BACKGROUND_ULTRA_DARK : "#fff",
            cardBackgroundTable: prefersDarkMode ? CARD_BACKGROUND_DARK : "#EEEEEE",
            tankContainer: "#D8D8D8",
            contrast: prefersDarkMode ? "#FFF" : "#000",
            backgroundLogo: prefersDarkMode ? "#FFF" : "rgba(255, 255, 255, 0.96)",
            tankContainerBackground: prefersDarkMode ? DARK_TANK_BACKGROUND : LIGHT_TANK_BACKGROUND,
          },
        },

        customPalette: {
          text: {
            primary: prefersDarkMode ? "#fff" : "#6E7878",
            darkgrey: prefersDarkMode ? "#fff" : "#4D4F5C",
            boldgrey: prefersDarkMode ? "#fff" : "#1D212A",
            lightgrey: "#B9BEB4",
            link: linkColor,
            linkHover: linkHoverColor,
            linkContrast: APP_WHITE,
            linkHoverContrast: "#B3CCDC",
            error: appError,
          },
          border: {
            lightgray: prefersDarkMode ? "#474747" : "#D7DAE2",
            gray: "#707070",
          },
          svg: {
            lightgrey: "#BCBCCB",
          },
          levels: {
            veryLow: LEVEL_COLOR_VERY_LOW,
            low: LEVEL_COLOR_LOW,
            normal: LEVEL_COLOR_NORMAL,
            high: "#6A6A6A",
          },
        },

        /** MATERIAL GLOBAL OVERRIDES**/
        overrides: {
          // custom Fonts
          MuiCssBaseline: {
            "@global": {
              "@font-face": [LINDE_REGULAR],
              "html": {
                background: prefersDarkMode ? CARD_BACKGROUND_ULTRA_DARK : "#F5F5F5",
              },
              "body": {
                backgroundColor: prefersDarkMode ? CARD_BACKGROUND_ULTRA_DARK : "#F5F5F5",
              },
            },
          },

          MuiTypography: {
            "root": {
              "font-face": [LINDE_REGULAR],
            },
          },

          MuiFormControl: {
            root: {
              "background": prefersDarkMode ? CARD_BACKGROUND_DARK : '#FFF',
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: RTUPrimaryColor,
              },

              "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                background: APP_WHITE,
                borderTop: selectBorder,
              },

              "& . MuiInput-input": {
                color: RTUPrimaryColor,
              },

              // "& .MuiInputBase-input": {
              //   color: prefersDarkMode ? "#FFF" : "#000",
              //   background: prefersDarkMode ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT,
              // },
              "& .MuiOutlinedInput-adornedEnd, & .MuiInputAdornment-positionEnd, & .MuiOutlinedInput-adornedEnd, & .MuiInputLabel-outlined.MuiInputLabel-shrink": {
                color: prefersDarkMode ? "#FFF" : "#000",
                background: `${prefersDarkMode ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT
                  } !important`,
                borderColor: "transparent",
              },
              "& .MuiInputLabel-animated": {
                color: RTUPrimaryColor,
              },
            },
          },

          // Native select
          // MuiSelect: {
          //   select: {
          //     "&:focus": {
          //       backgroundColor: APP_WHITE,
          //     },
          //   },
          // },

          MuiSwitch: {
            root: {
              "& .MuiSvgIcon-root": {
                marginTop: -2,
                background: prefersDarkMode ? CARD_BACKGROUND_LIGHT : CARD_BACKGROUND_DARK,
                borderRadius: 20,
                padding: 3,
              },
              "& .MuiSwitch-colorSecondary.Mui-checked > .MuiIconButton-label": {
                color: APP_PRIMARY_COLOR,
              },
              "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
                background: prefersDarkMode ? "#d9c9cf" : CARD_BACKGROUND_DARK,
              },
            },
          },

          MuiOutlinedInput: {
            root: {
              "borderRadius": "unset",

              "&:hover": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: RTUPrimaryColor,
                },
              },
            },
          },

          // MuiAccordion

          MuiLink: {
            button: {
              "color": linkColor,

              "&:hover": {
                color: linkHoverColor,
              },
            },
          },

          /*MuiPaper: {
            root: {
              background: `${prefersDarkMode ? CARD_BACKGROUND_DARK : "#fff"} !important`,
              color: `${prefersDarkMode ? "#fff" : "#000"} !important`
            }
          },*/
          MuiPopover: {
            root: {
              "&.MuiPaper-root .MuiMenu-paper .MuiPopover-paper .MuiPaper-elevation8 .MuiPaper-rounded": {
                marginTop: defaultTheme.spacing(5.5),
              },
            },
            paper: {
              "&.MuiPaper-root .MuiMenu-paper .MuiPopover-paper .MuiPaper-elevation8 .MuiPaper-rounded": {
                marginTop: defaultTheme.spacing(5.5),
              },
            },
          },

          MuiTooltip: {
            tooltip: {
              color: "rgba(0, 0, 0, 0.87)",
              fontSize: "1em",
              backgroundColor: APP_WHITE,
              borderRadius: "unset",
              border: selectBorder,
              boxShadow: defaultTheme.shadows[1],
              zIndex: 10000,
            },
          },

          MuiCard: {
            root: {
              "borderRadius": 0,
              "& .tankWrapper": {
                "color": "#717171",
                "& .text-small": {
                  color: prefersDarkMode ? "#FFF" : "#717171",
                },
                "& .text-large": {
                  color: prefersDarkMode ? "#FFF" : "#717171",
                },
              },
            },
          },

          MuiButton: {
            root: {
              borderRadius: 0,
              [defaultTheme.breakpoints.down("sm")]: {
                cursor: "pointer",
              },
            },

            contained: {
              backgroundColor: defaultTheme.palette.background.paper,
              fontWeight: "bold",
            },

            textSecondary: {
              color: prefersDarkMode ? "#fff" : "#4D4F5C",
              textDecoration: "underline",
            },
          },

          MuiIconButton: {
            root: {
              "color": "#717171",
              [defaultTheme.breakpoints.down("sm")]: {
                cursor: "pointer",
              },
              "& .MuiIconButton-edgeEnd": {
                background: prefersDarkMode ? CARD_BACKGROUND_DARK : CARD_BACKGROUND_LIGHT,
              },
            },

            colorSecondary: {
              color: "#717171",
            },
          },

          MuiPickerDTTabs: {
            tabs: {
              color: defaultTheme.palette.common.white,
            },
          },

          MuiPickersToolbarText: {
            toolbarTxt: {
              color: "rgba(255,255,255, 0.54)",
            },
            toolbarBtnSelected: {
              color: "rgba(255,255,255, 0.87)",
            },
          },

          MuiCheckbox: {
            colorSecondary: {
              "color": "#717171",
              "&$checked": {
                color: "#717171",
              },
            },
          },

          MuiTableRow: {
            root: {
              // FIXME - tirar !important
              "&.Mui-selected, .MuiTableRow-root.Mui-selected:hover": {
                backgroundColor: prefersDarkMode ? "#565555 !important" : "#F5F5F5 !important",
              },
            },
          },

          MuiDialogTitle: {
            root: {
              "fontWeight": "bold",
              "color": APP_PRIMARY_COLOR,
              "position": "relative",
              "backgroundColor": prefersDarkMode ? "#565555" : "#D7DAE2",
              "& > *": {
                fontWeight: "bold",
              },
            },
          },

          MuiDialogActions: {
            root: {
              "& .MuiButton-contained": {
                backgroundColor: prefersDarkMode ? "#565555" : "#D7DAE2",
                color: prefersDarkMode ? "#fff" : "#4D4F5C",
              },
            },
          },
        },

        /* MATERIAL GLOBAL DEFAULT PROPS OVERRIDES */
        props: {
          MuiTooltip: {
            [defaultTheme.breakpoints.up("sm")]: {
              placement: "right-start",
            },
            TransitionComponent: Zoom,
            arrow: true,
          },

          MuiAccordionSummary: {
            expandIcon: <ExpandMoreIcon fontSize="large" className={"icon"} />,
          },
        },
      }),
    [defaultTheme, prefersDarkMode]
  );

  RTUTheme = responsiveFontSizes(RTUTheme);

  return (
    <ThemeProvider theme={RTUTheme}>
      <CssBaseline />
      {props.children}
    </ThemeProvider>
  );
}

export default RTUThemeProvider;
